/**
 * General Feedback Form Component
 * Simple form for general comments and feedback
 */

import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Feedback, GeneralFeedback } from '../../../types/feedback';
import ScreenshotButton from '../ScreenshotButton';

interface GeneralFeedbackFormProps {
  data: Partial<Feedback>;
  onChange: (data: Partial<GeneralFeedback>) => void;
  validation: { isValid: boolean; errors: Record<string, string> };
  currentStep: number;
  totalSteps: number;
  enableScreenshot?: boolean;
}

const GeneralFeedbackForm: React.FC<GeneralFeedbackFormProps> = ({
  data,
  onChange,
  validation,
  enableScreenshot = true
}) => {
  const generalData = data as Partial<GeneralFeedback>;
  const [charCount, setCharCount] = useState(generalData.message?.length || 0);
  const [screenshot, setScreenshot] = useState<any>(null);

  const categories = [
    {
      key: 'praise',
      label: 'Praise',
      description: 'Positive feedback about what works well',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
        </svg>
      ),
      color: 'text-sage-600',
      bgColor: 'hover:bg-sage-50'
    },
    {
      key: 'criticism',
      label: 'Criticism',
      description: 'Constructive feedback about areas for improvement',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
        </svg>
      ),
      color: 'text-coral-600',
      bgColor: 'hover:bg-coral-50'
    },
    {
      key: 'suggestion',
      label: 'Suggestion',
      description: 'Ideas for new features or enhancements',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={10}/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <path d="M12 17h.01"/>
        </svg>
      ),
      color: 'text-steel-600',
      bgColor: 'hover:bg-steel-50'
    },
    {
      key: 'question',
      label: 'Question',
      description: 'Questions about features, data, or usage',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={10}/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <path d="M12 17h.01"/>
        </svg>
      ),
      color: 'text-amber-600',
      bgColor: 'hover:bg-amber-50'
    },
    {
      key: 'other',
      label: 'Other',
      description: 'Any other feedback or comments',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 12h.01"/>
          <path d="M12 12h.01"/>
          <path d="M16 12h.01"/>
        </svg>
      ),
      color: 'text-stone-600',
      bgColor: 'hover:bg-stone-50'
    }
  ];

  const handleMessageChange = (message: string) => {
    setCharCount(message.length);
    onChange({
      ...generalData,
      message,
      type: 'general'
    });
  };

  const handleCategoryChange = (category: GeneralFeedback['category']) => {
    onChange({
      ...generalData,
      category
    });
  };

  const handleSubjectChange = (subject: string) => {
    onChange({
      ...generalData,
      subject
    });
  };

  const handleScreenshotCapture = (screenshotData: any) => {
    setScreenshot(screenshotData);
    onChange({
      ...generalData,
      attachments: screenshotData.thumbnail ? [{
        type: 'screenshot',
        url: screenshotData.thumbnail,
        metadata: screenshotData.metadata
      }] : []
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-serif text-slate-800 mb-2">
          Share Your Thoughts
        </h3>
        <p className="text-sm text-slate-600">
          We value all feedback to help improve the CLEAR humanitarian platform
        </p>
      </div>

      <div className="space-y-4">
        {/* Subject */}
        <div>
          <label htmlFor="feedback-subject" className="block text-sm font-medium text-slate-700 mb-2">
            Subject (Optional)
          </label>
          <input
            id="feedback-subject"
            type="text"
            value={generalData.subject || ''}
            onChange={(e) => handleSubjectChange(e.target.value)}
            placeholder="Brief subject line for your feedback"
            className="input-minimal"
          />
        </div>

        {/* Category Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Category
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {categories.map((category) => (
              <button
                key={category.key}
                onClick={() => handleCategoryChange(category.key as GeneralFeedback['category'])}
                className={cn(
                  'flex items-center gap-3 p-3 text-left border-2 rounded-lg transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2',
                  generalData.category === category.key
                    ? 'border-terracotta bg-terracotta-50'
                    : 'border-stone-200 hover:border-stone-300',
                  category.bgColor
                )}
              >
                <div className={category.color}>
                  {category.icon}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 text-sm">
                    {category.label}
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5 leading-tight">
                    {category.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="feedback-message" className="block text-sm font-medium text-slate-700 mb-2">
            Your Message *
          </label>
          <div className="relative">
            <textarea
              id="feedback-message"
              rows={6}
              value={generalData.message || ''}
              onChange={(e) => handleMessageChange(e.target.value)}
              placeholder="Share your thoughts, questions, or feedback about the CLEAR platform..."
              className={cn(
                'input-minimal resize-none pr-16',
                validation.errors.message && 'border-coral-300 focus:border-coral-400'
              )}
              maxLength={2000}
            />
            <div className="absolute bottom-3 right-3 text-xs text-slate-400">
              {charCount}/2000
            </div>
          </div>
          {validation.errors.message && (
            <p className="text-sm text-coral-600 mt-1">{validation.errors.message}</p>
          )}
          <p className="text-xs text-slate-500 mt-2">
            Your feedback helps improve humanitarian data tools and response capabilities
          </p>
        </div>

        {/* Screenshot Button */}
        {enableScreenshot && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Add a screenshot (optional)
            </label>
            <ScreenshotButton
              onScreenshotCapture={handleScreenshotCapture}
              existingScreenshot={screenshot?.thumbnail}
            />
          </div>
        )}

        {/* Preview */}
        {generalData.message && generalData.message.trim().length > 0 && (
          <div className="bg-stone-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-slate-800 mb-2">Preview</h4>
            <div className="space-y-1 text-sm">
              {generalData.subject && (
                <p><span className="font-medium">Subject:</span> {generalData.subject}</p>
              )}
              {generalData.category && (
                <p><span className="font-medium">Category:</span> {categories.find(c => c.key === generalData.category)?.label}</p>
              )}
            </div>
            <div className="mt-2 p-3 bg-white rounded border-l-4 border-terracotta">
              <p className="text-sm text-slate-700 italic">
                "{generalData.message}"
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralFeedbackForm;